import { Controller } from "stimulus";
import { getJson } from "utils/json_request";
import * as messageRenderer from "live_chat/message_renderer";

// Read the next window once the playhead is this close to the end of what is loaded.
const PREFETCH_MS = 60 * 1000;
// A playhead this far past the loaded chat is a seek: reload around it rather than read up to it.
const SEEK_MS = 60 * 1000;
// This close to the video's end, playback has reached it and the afterparty chat shows.
const END_MS = 500;
const RETRY_MS = 5000;
// Same as the live chat panel: within this of the bottom still counts as following along.
const PINNED_PX = 40;
// The panel's height range, in rem: as tall as fits below it, so the player above stays in view.
const PANEL_MIN_REM = 18;
const PANEL_MAX_REM = 28;

const DIVIDER_LABELS = {
  before: "Before the premiere",
  during: "The premiere begins",
  after: "After the premiere",
};

// Read-only live chat replay, revealed in step with the episode's video player: every archived
// message at or before the playhead (the whole afterparty once playback reaches the end), with
// older chat read back as the viewer scrolls up. Listens to the player only while open.
export default class extends Controller {
  static targets = ["card", "openButton", "panel", "closeButton", "messages", "placeholder", "placeholderText", "retry", "messageTemplate", "moderatorBadge", "divider"];
  static values = { url: String, itemId: String, durationMs: Number };

  // Always starts closed, even from a Turbo snapshot taken while it was open: the chat that
  // snapshot shows is not being followed any more.
  connect() {
    this.onTimeUpdate = this.onTimeUpdate.bind(this);
    this.onSeeking = this.onSeeking.bind(this);
    this.generation = 0;
    this.reset();
    this.showCard();
  }

  disconnect() {
    this.stopListening();
    this.reset();
  }

  open() {
    this.cardTarget.hidden = true;
    this.panelTarget.hidden = false;
    this.openButtonTarget.setAttribute("aria-expanded", "true");
    this.fitPanel();
    this.closeButtonTarget.focus({ preventScroll: true });
    window.addEventListener("supercast-timeupdate", this.onTimeUpdate);
    // seeking doesn't bubble, so it is caught on its way down to the player's <video>.
    document.addEventListener("seeking", this.onSeeking, true);

    const video = this.video();
    this.jumpTo(video ? video.currentTime * 1000 : 0, video ? this.reachedEnd(video.currentTime, video.duration) : false);
  }

  close() {
    this.stopListening();
    this.reset();
    this.showCard();
    this.openButtonTarget.focus();
  }

  showCard() {
    this.panelTarget.hidden = true;
    this.cardTarget.hidden = false;
    this.openButtonTarget.setAttribute("aria-expanded", "false");
  }

  retry() {
    this.jumpTo(this.playheadMs, this.ended);
  }

  loadOlder() {
    if (this.messagesTarget.scrollTop <= PINNED_PX) this.readBack();
  }

  stopListening() {
    window.removeEventListener("supercast-timeupdate", this.onTimeUpdate);
    document.removeEventListener("seeking", this.onSeeking, true);
  }

  // Also drops every response still in flight: each checks it belongs to the current generation.
  reset() {
    this.generation += 1;
    if (this.abortController) this.abortController.abort();
    clearTimeout(this.retryTimer);
    this.abortController = new AbortController();
    this.messages = [];
    this.ids = new Set();
    this.shown = 0;
    this.bounds = null;
    this.historyLoaded = false;
    this.readingForward = false;
    this.readingBack = false;
    this.failed = false;
    this.retryAt = 0;
    this.messagesTarget.replaceChildren();
  }

  onTimeUpdate(event) {
    const detail = event.detail || {};
    if (detail.mediaType !== "video" || detail.itemType !== "Episode" || String(detail.itemId) !== this.itemIdValue) return;

    this.moveTo(detail.currentTime * 1000, this.reachedEnd(detail.currentTime, detail.duration));
  }

  onSeeking(event) {
    const video = this.video();
    if (!video || event.target !== video) return;

    this.moveTo(video.currentTime * 1000, this.reachedEnd(video.currentTime, video.duration));
  }

  moveTo(ms, ended) {
    if (!Number.isFinite(ms)) return;
    // Once every message ahead is loaded, loadedTo stops moving, so being past it is no seek.
    if (ms < this.loadedFrom || (ms > this.loadedTo + SEEK_MS && !this.forwardDone())) return this.jumpTo(ms, ended);

    this.playheadMs = ms;
    this.ended = ended;
    this.render();
  }

  // Starts over around a playhead: the chat just before it, read back, and what comes after, read forward.
  jumpTo(ms, ended) {
    this.reset();
    this.playheadMs = ms;
    this.ended = ended;
    this.loadedFrom = this.loadedTo = Math.floor(ms);
    this.render();
    this.readBack();
  }

  render() {
    const limit = this.ended ? Infinity : this.playheadMs;
    let count = this.shown;
    while (count < this.messages.length && this.messages[count].offset_ms <= limit) count += 1;
    while (count > 0 && this.messages[count - 1].offset_ms > limit) count -= 1;

    if (count < this.shown) {
      this.truncate(count);
      this.scrollToBottom();
    } else if (count > this.shown) {
      const pinned = this.isPinnedToBottom();
      const rows = document.createDocumentFragment();
      for (let index = this.shown; index < count; index += 1) this.appendRow(rows, index);
      this.messagesTarget.appendChild(rows);
      this.shown = count;
      if (pinned) this.scrollToBottom();
    }

    this.updatePlaceholder();
    if (this.ended || this.loadedTo - this.playheadMs < PREFETCH_MS) this.readForward();
  }

  async readForward() {
    if (this.readingForward || !this.historyLoaded || this.forwardDone() || Date.now() < this.retryAt) return;

    this.readingForward = true;
    const generation = this.generation;
    const page = await this.read({ from_ms: this.loadedTo });
    if (generation !== this.generation) return;

    this.readingForward = false;
    if (!page) return;

    this.loadedTo = page.to_ms;
    this.append(page.messages);
    this.render();
  }

  // Also the first read after a jump: until it lands the panel shows nothing, so the rows it adds
  // above the playhead never arrive under ones already on screen.
  async readBack() {
    if (this.readingBack || this.backDone() || Date.now() < this.retryAt) return;

    this.readingBack = true;
    const firstRead = !this.historyLoaded;
    const generation = this.generation;
    const page = await this.read({ before_ms: this.loadedFrom });
    if (generation !== this.generation) return;

    this.readingBack = false;
    if (!page) return;

    this.loadedFrom = page.from_ms;
    const fresh = page.messages.filter((message) => !this.ids.has(message.id));
    fresh.forEach((message) => this.ids.add(message.id));
    this.messages = fresh.concat(this.messages);

    if (firstRead) {
      this.historyLoaded = true;
      this.render();
      this.scrollToBottom();
    } else {
      // Keep the rows already on screen where they are while older ones land above them.
      const fromBottom = this.messagesTarget.scrollHeight - this.messagesTarget.scrollTop;
      this.prepend(fresh.length);
      this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight - fromBottom;
    }
  }

  // Resolves with the page, or with nothing when it failed (retried after RETRY_MS) or was
  // overtaken by a jump or close, which the caller checks for itself.
  async read(query) {
    const generation = this.generation;
    try {
      const page = await getJson(`${this.urlValue}?${new URLSearchParams(query)}`, { signal: this.abortController.signal });
      if (generation !== this.generation) return null;

      this.bounds = { first: page.first_ms, last: page.last_ms };
      this.failed = false;
      return page;
    } catch {
      if (generation !== this.generation) return null;

      this.failed = true;
      this.retryAt = Date.now() + RETRY_MS;
      this.updatePlaceholder();
      // Retries otherwise ride the player's timeupdates, which stop once playback ends or pauses.
      clearTimeout(this.retryTimer);
      this.retryTimer = setTimeout(() => this.render(), RETRY_MS);
      return null;
    }
  }

  forwardDone() {
    return this.bounds !== null && (this.bounds.last === null || this.loadedTo > this.bounds.last);
  }

  backDone() {
    return this.bounds !== null && (this.bounds.first === null || this.loadedFrom <= this.bounds.first);
  }

  append(messages) {
    messages.forEach((message) => {
      if (this.ids.has(message.id)) return;
      this.ids.add(message.id);
      this.messages.push(message);
    });
  }

  // Only the first `count` messages were just added to this.messages: builds just their rows, and
  // redoes the divider where they meet the rows already on screen.
  prepend(count) {
    const first = this.messagesTarget.firstElementChild;
    if (first && first.hasAttribute("data-chat-replay-divider")) first.remove();

    const rows = document.createDocumentFragment();
    for (let index = 0; index < count; index += 1) this.appendRow(rows, index);
    if (this.shown > 0) this.appendDivider(rows, count);
    this.messagesTarget.prepend(rows);
    this.shown += count;
  }

  // A backward seek within what is loaded: removes the rows from the first message now past the
  // playhead onward, with the divider that heads it.
  truncate(count) {
    let node = this.messagesTarget.querySelector(`[data-message-id="${CSS.escape(this.messages[count].id)}"]`);
    if (node && node.previousElementSibling && node.previousElementSibling.hasAttribute("data-chat-replay-divider")) node = node.previousElementSibling;
    while (node) {
      const next = node.nextElementSibling;
      node.remove();
      node = next;
    }
    this.shown = count;
  }

  // A divider goes wherever the chat crosses from the waiting room into the premiere, or into the
  // afterparty, and heads the list when it opens on either.
  appendRow(parent, index) {
    this.appendDivider(parent, index);
    parent.appendChild(this.buildRow(this.messages[index]));
  }

  appendDivider(parent, index) {
    const section = this.sectionOf(this.messages[index]);
    const previous = index > 0 ? this.sectionOf(this.messages[index - 1]) : "during";
    if (section !== previous) parent.appendChild(this.buildDivider(section));
  }

  sectionOf(message) {
    if (message.offset_ms < 0) return "before";
    if (this.durationMsValue > 0 && message.offset_ms >= this.durationMsValue) return "after";
    return "during";
  }

  buildRow(message) {
    const row = this.messageTemplateTarget.content.cloneNode(true).firstElementChild;
    row.dataset.messageId = message.id;
    messageRenderer.fillAvatar(row, message.user);
    messageRenderer.fillIdentity(row, message.user.name || "", { isChannelAdmin: message.channel_admin, badgeTemplate: this.moderatorBadgeTarget });
    messageRenderer.fillBody(row.querySelector("[data-chat-message-body]"), message.body, message.moderator);
    return row;
  }

  buildDivider(section) {
    const divider = this.dividerTarget.content.cloneNode(true).firstElementChild;
    divider.setAttribute("data-chat-replay-divider", section);
    divider.querySelector("[data-chat-replay-divider-label]").textContent = DIVIDER_LABELS[section];
    return divider;
  }

  updatePlaceholder() {
    const empty = this.shown === 0;
    this.placeholderTarget.hidden = !empty;
    if (!empty) return;

    this.retryTarget.hidden = !this.failed;
    if (this.failed) {
      this.placeholderTextTarget.textContent = "Chat replay couldn't load.";
    } else if (!this.historyLoaded) {
      this.placeholderTextTarget.textContent = "Loading chat…";
    } else {
      this.placeholderTextTarget.textContent = "Messages will appear as the video plays.";
    }
  }

  // The newest messages land at the panel's bottom edge, so all of it has to be on screen: only
  // when even the shortest panel doesn't fit below the player does the page scroll to it.
  fitPanel() {
    const rem = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    const room = window.innerHeight - this.panelTarget.getBoundingClientRect().top - rem;
    this.panelTarget.style.height = `${Math.round(Math.min(PANEL_MAX_REM * rem, Math.max(PANEL_MIN_REM * rem, room)))}px`;
    this.panelTarget.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }

  reachedEnd(currentTime, duration) {
    return duration > 0 && currentTime * 1000 >= duration * 1000 - END_MS;
  }

  // The episode's player, found by the item it plays (Media::VideoPlayerComponent's data-item-*).
  video() {
    return document.querySelector(`[data-item-type="Episode"][data-item-id="${CSS.escape(this.itemIdValue)}"] :is(video, hlsjs-video)`);
  }

  isPinnedToBottom() {
    const el = this.messagesTarget;
    return el.scrollHeight - el.scrollTop - el.clientHeight < PINNED_PX;
  }

  scrollToBottom() {
    this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight;
  }
}
